import {
  DownOutlined,
  ExportOutlined,
  PlusOutlined,
  RightOutlined,
} from '@ant-design/icons'
import { useRouter } from '@tanstack/react-router'
import { Button, Collapse, message, Skeleton } from 'antd'
import { useRolodexEdit } from 'features/fde/rolodexAPI'
import { UnresolvedCommentBadge } from 'features/libra/comments/UnresolvedCommentBadge'
import { CreateVersionModal } from 'features/libra/dashboard/CreateVersionModal'
import { substringMatch } from 'features/libra/helpers/fuzzyMatch'
import {
  getLifecycleStage,
  isTerminalStage,
} from 'features/libra/lifecycle/lifecycleStageHelpers'
import {
  getDefaultStage,
  type LifecycleConfig,
} from 'features/libra/lifecycle/lifecycleTypes'
import { unwrapFieldValue } from 'features/libra/ontology/ontologyFieldDefaults'
import {
  type LibraTicket,
  useLibraWorkflows,
} from 'features/libra/ticket/libraTicketsApi'
import {
  ELIGIBILITY_STATUS,
  ELIGIBILITY_UI_LABELS,
  type EligibilityStatus,
} from 'features/libra/types/eligibility'
import type { LibraWorkflow } from 'features/libra/workflow/workflowStepsApi'
import { useRolodexEntries } from 'features/rolodex'
import isNil from 'lodash/isNil'
import uniq from 'lodash/uniq'
import type { FC } from 'react'
import { useCallback, useMemo, useState } from 'react'
import { createLibraSearch } from 'routes/libra/-searchParams'
import styled from 'styled-components'
import { RolodexEntryType } from 'types'
import { groupBy } from 'utils/fde/maps'
import { displayNameFromEmail } from 'utils/stringHelpers'

import { TicketAccordionContent } from './TicketAccordionContent'
import { useAssetStats } from './useAssetStats'

function renderTicketStatusBadge(
  ticket: LibraTicket,
  workflow: LibraWorkflow | undefined,
  versionNumber: number | undefined
) {
  const stage = getLifecycleStage(ticket, workflow?.lifecycleConfig)
  if (stage) {
    if (stage.terminal) {
      return (
        <ApprovedBadge>
          {stage.name}
          {versionNumber !== undefined ? ` (V${versionNumber})` : ''}
        </ApprovedBadge>
      )
    }
    return (
      <PrimaryStateBadge>
        {stage.name}
        {versionNumber !== undefined ? ` (V${versionNumber})` : ''}
      </PrimaryStateBadge>
    )
  }
  if (ticket.workflowComplete) {
    return (
      <PrimaryStateBadge>
        Complete{versionNumber !== undefined ? ` (V${versionNumber})` : ''}
      </PrimaryStateBadge>
    )
  }
  return versionNumber !== undefined ? (
    <SecondaryStateBadge>Draft (V{versionNumber})</SecondaryStateBadge>
  ) : null
}

type AssetListViewProps = {
  tickets: ReadonlyArray<LibraTicket>
  allTickets: ReadonlyArray<LibraTicket>
  statsTickets?: ReadonlyArray<LibraTicket>
  isLoading: boolean
  onRefresh: () => Promise<void>
  filterText?: string
  selectedEligibility?: EligibilityStatus
  selectedCreators?: string[]
  selectedStage?: string
  lifecycleConfig?: LifecycleConfig
  canCreateVersion?: (ticket: LibraTicket) => boolean
  unresolvedCounts?: ReadonlyMap<string, number>
}

function compareTicketRecency(a: LibraTicket, b: LibraTicket): number {
  const aVersion = getTicketVersionValue(a)
  const bVersion = getTicketVersionValue(b)

  if (aVersion !== bVersion) {
    return bVersion - aVersion
  }

  const updatedCompare = (b.updatedAt ?? '').localeCompare(a.updatedAt ?? '')
  if (updatedCompare !== 0) {
    return updatedCompare
  }

  return (b.createdAt ?? '').localeCompare(a.createdAt ?? '')
}

function getTicketVersionValue(ticket: LibraTicket): number {
  const rawVersion = ticket.version
  return typeof rawVersion === 'number' && Number.isFinite(rawVersion)
    ? rawVersion
    : 1
}

function getAssessmentKey(ticket: LibraTicket): string {
  return ticket.assessmentId || ticket.id
}

function getEffectiveStageId(
  ticket: LibraTicket,
  knownStageIds: ReadonlySet<string>,
  defaultStageId: string | undefined
): string | undefined {
  const stageId = ticket.ticketStatus || ''
  if (knownStageIds.has(stageId)) return stageId
  return defaultStageId
}

export const AssetListView: FC<AssetListViewProps> = ({
  tickets,
  allTickets,
  statsTickets,
  isLoading,
  onRefresh,
  filterText = '',
  selectedEligibility,
  selectedCreators,
  selectedStage,
  lifecycleConfig: lcConfig,
  canCreateVersion: canCreateVersionForUser,
  unresolvedCounts,
}) => {
  const router = useRouter()
  const { rolodexDelete } = useRolodexEdit()
  const [messageApi, contextHolder] = message.useMessage()
  const [ticketForNewVersion, setTicketForNewVersion] = useState<
    LibraTicket | undefined
  >()
  const [deletingTicketId, setDeletingTicketId] = useState<string | undefined>()

  const ticketsForStats = statsTickets ?? tickets

  const getAssetEligibility = useMemo(() => {
    return (unitId: string): EligibilityStatus => {
      const unitTickets = ticketsForStats.filter(
        (ticket) => ticket.tags.unit === unitId
      )

      const eligibilityValues = unitTickets
        .map((ticket) =>
          unwrapFieldValue<string>(ticket.fieldValues?.eligibility)
        )
        .filter((value): value is string => !isNil(value))

      if (eligibilityValues.length === 0) return ELIGIBILITY_STATUS.UNCLEAR

      if (
        eligibilityValues.some((value) => value === ELIGIBILITY_STATUS.ELIGIBLE)
      ) {
        return ELIGIBILITY_STATUS.ELIGIBLE
      }

      return ELIGIBILITY_STATUS.NOT_ELIGIBLE
    }
  }, [ticketsForStats])

  const { assetStats, isLoading: statsLoading } = useAssetStats(
    ticketsForStats,
    getAssetEligibility
  )
  const workflowIds = useMemo(
    () =>
      uniq(
        tickets
          .map((ticket) => ticket.workflowId)
          .filter((id): id is string => id !== undefined)
      ),
    [tickets]
  )
  const { libraWorkflows, isLoading: workflowsLoading } = useLibraWorkflows(
    useMemo(() => ({ ids: workflowIds }), [workflowIds])
  )
  const workflowMap = useMemo(
    () => new Map(libraWorkflows.map((workflow) => [workflow.id, workflow])),
    [libraWorkflows]
  )

  const { getUnit, isLoading: unitsLoading } = useRolodexEntries(
    useMemo(
      () => ({
        type: RolodexEntryType.UNIT,
        ids: assetStats.map((asset) => asset.unitId),
      }),
      [assetStats]
    )
  )

  const ticketsByUnit = useMemo(
    () => groupBy({ arr: tickets, key: 'tags.unit' }),
    [tickets]
  )

  const filteredAssetStats = useMemo(() => {
    const query = filterText.trim().toLowerCase()
    let filtered = assetStats

    if (query) {
      filtered = filtered.filter((asset) => {
        const unitName = getUnit(asset.unitId)?.data.name ?? ''
        return substringMatch(unitName, query)
      })
    }

    if (selectedEligibility) {
      filtered = filtered.filter(
        (asset) => asset.eligibility === selectedEligibility
      )
    }

    if (selectedCreators && selectedCreators.length > 0) {
      const creatorSet = new Set(selectedCreators)
      filtered = filtered.filter((asset) => {
        const unitTickets = ticketsByUnit.get(asset.unitId) ?? []
        return unitTickets.some(
          (ticket) =>
            ticket.createdBy !== null && creatorSet.has(ticket.createdBy)
        )
      })
    }

    if (selectedStage && lcConfig) {
      const knownStageIds = new Set(lcConfig.stages.map((s) => s.id))
      const defaultStageId = getDefaultStage(lcConfig)?.id
      filtered = filtered.filter((asset) => {
        const unitTickets = ticketsByUnit.get(asset.unitId) ?? []
        return unitTickets.some(
          (ticket) =>
            getEffectiveStageId(ticket, knownStageIds, defaultStageId) ===
            selectedStage
        )
      })
    }

    return [...filtered].sort((a, b) => {
      const aName = getUnit(a.unitId)?.data.name ?? a.unitId
      const bName = getUnit(b.unitId)?.data.name ?? b.unitId
      return aName.localeCompare(bName)
    })
  }, [
    assetStats,
    filterText,
    selectedEligibility,
    selectedCreators,
    selectedStage,
    lcConfig,
    ticketsByUnit,
    getUnit,
  ])

  const isBusy = isLoading || unitsLoading || statsLoading || workflowsLoading

  const getTicketsForAsset = useCallback(
    (unitId: string) => tickets.filter((ticket) => ticket.tags.unit === unitId),
    [tickets]
  )
  const getAllTicketsForAsset = useCallback(
    (unitId: string) =>
      allTickets.filter((ticket) => ticket.tags.unit === unitId),
    [allTickets]
  )

  const handleTicketClick = useCallback(
    (ticket: LibraTicket) => {
      const orgId = ticket.tags.organization as string | undefined
      const workflowId = ticket.workflowId
      if (!orgId || !workflowId) return

      router.navigate({
        to: '/libra',
        search: createLibraSearch({
          orgId,
          workflowId,
          ticket: ticket.id,
          thread: undefined,
        }),
      })
    },
    [router]
  )

  const handleVersionCreated = useCallback(
    async (newTicketId: string, sourceTicket: LibraTicket) => {
      const orgId = sourceTicket.tags.organization as string | undefined
      const workflowId = sourceTicket.workflowId
      if (!orgId || !workflowId) {
        return
      }

      try {
        // Refetch ticket query data before navigation so the newly created
        // version is present in client state when the ticket page resolves
        // `?ticket=<newTicketId>`.
        await onRefresh()
      } catch (error) {
        console.warn('Failed to refresh dashboard before navigation', error)
      }
      router.navigate({
        to: '/libra',
        search: createLibraSearch({
          orgId,
          workflowId,
          ticket: newTicketId,
          thread: undefined,
        }),
      })
      setTicketForNewVersion(undefined)
    },
    [onRefresh, router]
  )

  const handleDeleteLatestVersion = useCallback(
    async (ticket: LibraTicket) => {
      try {
        setDeletingTicketId(ticket.id)
        await rolodexDelete(ticket.id)
        await onRefresh()
        messageApi.success('Deleted latest in-progress version')
      } catch (error) {
        console.error('Failed to delete latest version', error)
        messageApi.error('Failed to delete latest version')
      } finally {
        setDeletingTicketId(undefined)
      }
    },
    [messageApi, onRefresh, rolodexDelete]
  )

  return (
    <Container>
      {contextHolder}
      <AssetList>
        {isBusy ? (
          <SkeletonContainer>
            <Skeleton active title={false} paragraph={{ rows: 8 }} />
          </SkeletonContainer>
        ) : (
          <StyledCollapse
            expandIcon={({ isActive }) =>
              isActive ? <DownOutlined /> : <RightOutlined />
            }
            items={filteredAssetStats.map((asset) => {
              const assetTickets = getTicketsForAsset(asset.unitId)
              const assetAllTickets = getAllTicketsForAsset(asset.unitId)
              const latestAssetTicket = [...assetAllTickets].sort(
                compareTicketRecency
              )[0]
              const statsSourceTicket = [...ticketsForStats]
                .filter((ticket) => ticket.tags.unit === asset.unitId)
                .sort(compareTicketRecency)[0]
              const latestCompletedTicket = [...assetAllTickets]
                .filter((ticket) => ticket.workflowComplete)
                .sort(compareTicketRecency)[0]
              const openTargetTicket =
                latestCompletedTicket ?? latestAssetTicket
              const stableVersionTicket = statsSourceTicket?.workflowComplete
                ? statsSourceTicket
                : undefined
              const activeVersionTicket = stableVersionTicket
                ? [...assetAllTickets]
                    .filter(
                      (ticket) =>
                        getAssessmentKey(ticket) ===
                        getAssessmentKey(stableVersionTicket)
                    )
                    .sort(compareTicketRecency)[0]
                : latestAssetTicket
              const hasUpdateInProgress =
                stableVersionTicket !== undefined &&
                activeVersionTicket !== undefined &&
                getTicketVersionValue(activeVersionTicket) >
                  getTicketVersionValue(stableVersionTicket) &&
                !activeVersionTicket.workflowComplete
              const stableVersionNumber = stableVersionTicket
                ? getTicketVersionValue(stableVersionTicket)
                : undefined
              const activeVersionNumber = activeVersionTicket
                ? getTicketVersionValue(activeVersionTicket)
                : undefined
              const hasSingleTicket = assetTickets.length === 1
              const singleTicket = hasSingleTicket ? assetTickets[0] : undefined
              const stableWorkflow = stableVersionTicket
                ? workflowMap.get(stableVersionTicket.workflowId)
                : undefined
              const canCreateVersion = singleTicket
                ? isTerminalStage(
                    singleTicket,
                    workflowMap.get(singleTicket.workflowId)?.lifecycleConfig
                  ) &&
                  (canCreateVersionForUser?.(singleTicket) ?? true)
                : false

              const handleButtonClick = (e: React.MouseEvent) => {
                if (hasSingleTicket && openTargetTicket) {
                  e.stopPropagation()
                  handleTicketClick(openTargetTicket)
                }
              }

              const handleUpdateButtonClick = (e: React.MouseEvent) => {
                if (!singleTicket) {
                  return
                }
                e.stopPropagation()
                setTicketForNewVersion(singleTicket)
              }

              return {
                key: asset.unitId,
                label: (
                  <AssetCardRow>
                    <AssetCardHeader>
                      <AssetName>
                        {getUnit(asset.unitId)?.data.name ?? asset.unitId}
                      </AssetName>
                      <AssetStatsRow>
                        <StatBadge>
                          {assetTickets.length} ticket
                          {assetTickets.length > 1 ? 's' : ''}
                        </StatBadge>
                        {stableVersionTicket ? (
                          renderTicketStatusBadge(
                            stableVersionTicket,
                            stableWorkflow,
                            stableVersionNumber
                          )
                        ) : activeVersionNumber !== undefined ? (
                          <SecondaryStateBadge>
                            Draft (V{activeVersionNumber})
                          </SecondaryStateBadge>
                        ) : null}
                        <EligibilityBadge $eligibility={asset.eligibility}>
                          {ELIGIBILITY_UI_LABELS[asset.eligibility]}
                        </EligibilityBadge>
                        <UnresolvedCommentBadge
                          count={unresolvedCounts?.get(
                            latestAssetTicket?.id ?? ''
                          )}
                        />
                        {hasUpdateInProgress &&
                          activeVersionNumber !== undefined && (
                            <SecondaryStateBadge>
                              Update in Progress (V{activeVersionNumber})
                            </SecondaryStateBadge>
                          )}
                        {latestAssetTicket?.createdBy && (
                          <CreatorText>
                            by{' '}
                            {displayNameFromEmail(latestAssetTicket.createdBy)}
                          </CreatorText>
                        )}
                      </AssetStatsRow>
                    </AssetCardHeader>
                    <ActionButtons>
                      <ActionButton
                        type={hasSingleTicket ? 'primary' : 'default'}
                        size='small'
                        icon={hasSingleTicket ? <ExportOutlined /> : undefined}
                        onClick={handleButtonClick}>
                        {hasSingleTicket
                          ? 'Open'
                          : `${asset.ticketCount} Tickets`}
                      </ActionButton>
                      {canCreateVersion && (
                        <SecondaryActionButton
                          type='text'
                          size='small'
                          icon={<PlusOutlined />}
                          onClick={handleUpdateButtonClick}>
                          New Version
                        </SecondaryActionButton>
                      )}
                    </ActionButtons>
                  </AssetCardRow>
                ),
                children: (
                  <TicketAccordionContent
                    tickets={assetTickets}
                    allTickets={assetAllTickets}
                    workflowMap={workflowMap}
                    onTicketClick={handleTicketClick}
                    onCreateVersion={setTicketForNewVersion}
                    onDeleteLatestVersion={handleDeleteLatestVersion}
                    deletingTicketId={deletingTicketId}
                    canCreateVersion={canCreateVersionForUser}
                    unresolvedCounts={unresolvedCounts}
                  />
                ),
              }
            })}
          />
        )}
        {!isBusy && filteredAssetStats.length === 0 && (
          <EmptyState>No assets found</EmptyState>
        )}
      </AssetList>
      <CreateVersionModal
        isOpen={Boolean(ticketForNewVersion)}
        sourceTicket={ticketForNewVersion}
        onClose={() => setTicketForNewVersion(undefined)}
        onSuccess={handleVersionCreated}
      />
    </Container>
  )
}

// Styled components
const Container = styled.div`
  display: flex;
  flex-direction: column;
`

const AssetList = styled.div`
  flex: 1;
  padding: 0 0.5rem 1rem;
  overflow: visible;
`

const SkeletonContainer = styled.div`
  padding: 1rem;
`

const StyledCollapse = styled(Collapse)`
  background: transparent;
  border: none;
  overflow: visible;

  .ant-collapse-item {
    position: relative;
    margin-bottom: 0.5rem;
    border: 1px solid ${({ theme }) => theme.colors.gray[700]};
    border-radius: 6px !important;
    overflow: hidden;
    background: ${({ theme }) => theme.colors.gray[800]};

    &::after {
      content: '';
      position: absolute;
      inset: 0;
      border: 1px solid ${({ theme }) => theme.forecast.primary};
      border-radius: 6px;
      opacity: 0;
      pointer-events: none;
      transition: opacity 0.2s ease;
      z-index: 1;
    }

    &:hover {
      border-color: ${({ theme }) => theme.colors.gray[700]};
      background: ${({ theme }) => theme.colors.gray[700]};
    }

    &:hover::after {
      opacity: 1;
    }
  }

  .ant-collapse-item:last-child {
    margin-bottom: 1rem;
  }

  .ant-collapse-header {
    padding: 0.75rem !important;
    align-items: flex-start !important;
    color: ${({ theme }) => theme.forecast.activeText} !important;
  }

  .ant-collapse-expand-icon {
    color: ${({ theme }) => theme.forecast.defaultText};
    padding-top: 0.25rem !important;
  }

  .ant-collapse-panel {
    background: ${({ theme }) => theme.forecast.pageBackground} !important;
    border-top: 1px solid ${({ theme }) => theme.colors.gray[600]};
    overflow: visible;
  }

  .ant-collapse-content {
    overflow: visible;
  }

  .ant-collapse-content-box {
    padding: 0.5rem 0.5rem 0.75rem !important;
    overflow: visible;
  }
`

const AssetCardRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex: 1;
`

const AssetCardHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  flex: 1;
  min-width: 0;
`

const ActionButton = styled(Button)`
  flex-shrink: 0;
`

const ActionButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 0.35rem;
`

const SecondaryActionButton = styled(ActionButton)`
  color: ${({ theme }) => theme.forecast.secondaryText} !important;
  &:hover {
    color: ${({ theme }) => theme.forecast.activeText} !important;
  }
`

const AssetName = styled.div`
  font-weight: 500;
  color: ${({ theme }) => theme.forecast.activeText};
  font-size: 0.875rem;
`

const AssetStatsRow = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`

const StatBadge = styled.span`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.forecast.defaultText};
  background: ${({ theme }) => theme.colors.gray[700]};
  padding: 0.125rem 0.5rem;
  border-radius: 10px;
`

const PrimaryStateBadge = styled.span`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.common.badge.text.green};
  background: ${({ theme }) => theme.common.badge.background.green};
  padding: 0.125rem 0.5rem;
  border-radius: 10px;
  font-weight: 600;
`

const EmptyState = styled.div`
  text-align: center;
  padding: 2rem;
  color: ${({ theme }) => theme.forecast.secondaryText};
`

const EligibilityBadge = styled.span<{ $eligibility: EligibilityStatus }>`
  font-size: 0.75rem;
  color: ${({ $eligibility, theme }) =>
    $eligibility === ELIGIBILITY_STATUS.ELIGIBLE
      ? theme.common.badge.text.green
      : $eligibility === ELIGIBILITY_STATUS.NOT_ELIGIBLE
        ? theme.forecast.danger
        : theme.common.badge.text.orange};
  background: ${({ theme }) => theme.colors.gray[700]};
  padding: 0.125rem 0.5rem;
  border-radius: 10px;
  font-weight: 500;
`

const SecondaryStateBadge = styled.span`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.common.badge.text.orange};
  background: ${({ theme }) => theme.common.badge.background.orange};
  padding: 0.125rem 0.5rem;
  border-radius: 10px;
  font-weight: 600;
`

const ApprovedBadge = styled.span`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.common.badge.text.green};
  background: ${({ theme }) => theme.common.badge.background.green};
  padding: 0.125rem 0.5rem;
  border-radius: 10px;
  font-weight: 600;
`

const CreatorText = styled.span`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.forecast.secondaryText};
`
